import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldCheck, FileText, AlertTriangle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import TurnstileField from "../components/TurnstileField";
import { api, apiErrorMessage } from "../lib/api";
import { WARN_CHECKSUM, checksumMatches } from "../lib/cnp";
import { isFped } from "../lib/regime";

/**
 * `/date-curs/:token` — the fields the Ministry of Education requires for the
 * accreditation file, collected after payment rather than during it.
 *
 * Asking for a CNP on the checkout form costs enrolments, so this arrives an
 * hour later, by email, to somebody who has already paid and whose motivation
 * is therefore at its highest.
 *
 * Three things about it are deliberate and easy to undo by accident:
 *
 *   * The token in the URL is the whole identity. It names one enrollment and
 *     grants nothing else.
 *   * Most fields arrive prefilled from what the person typed at checkout.
 *     Confirming them is worth as much as collecting the new ones — it turns
 *     data we have been assuming into data they have just looked at.
 *   * The CNP is never read back. Links get forwarded; somebody holding
 *     another person's link already sees the name and school that the payment
 *     link exposes, and a national identification number is a different
 *     matter. If they return after submitting, the page says so and shows
 *     nothing.
 */

const FIELD_CLS =
  "w-full rounded-lg border border-[#E5E7EB] bg-[#F5F6F6] px-4 py-3 text-sm text-[#373A45] " +
  "outline-none focus:bg-white focus:border-[#965EA7] transition-colors";

function Label({ children, required }) {
  return (
    <span className="block text-xs font-semibold uppercase tracking-wide text-[#6B7280] mb-1.5">
      {children}
      {/* Non-breaking space: with an ordinary one the asterisk wraps onto a
          line of its own, which makes the label two lines tall and drops the
          field below its neighbours in the row. */}
      {required && <span className="text-[#965EA7]">{"\u00A0*"}</span>}
    </span>
  );
}

function Shell({ children, fped = false }) {
  return (
    <div className="min-h-screen bg-[#F5F6F6] flex flex-col" data-testid="ministry-data-page">
      <SEO title={fped ? "Date pentru contract și certificat | FPED" : "Date pentru dosarul de acreditare | FPED"} noindex />
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 lg:px-8 py-12 md:py-16">
        {children}
      </main>
      <Footer />
    </div>
  );
}

const EMPTY = {
  nume: "", initiala_tatalui: "", prenume: "", cnp: "",
  functie_didactica: "", specializare: "", unitate: "", city: "", county: "",
};

export default function DateCurs() {
  const { token } = useParams();
  const [page, setPage] = useState(null);     // null = loading, false = bad link
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const turnstileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/ministry-data/${token}`)
      .then((r) => {
        if (cancelled) return;
        setPage(r.data);
        setForm((f) => ({ ...f, ...(r.data.prefill || {}) }));
      })
      .catch(() => { if (!cancelled) setPage(false); });
    return () => { cancelled = true; };
  }, [token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // `null` until thirteen digits are present, so the warning appears when
  // there is something to warn about rather than on the fourth keystroke.
  const cnpLooksWrong = checksumMatches(form.cnp) === false;

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Te rugăm să confirmi că ai citit informarea de mai sus.");
      return;
    }
    if (!turnstileToken) {
      toast.error("Te rugăm să aștepți verificarea anti-spam.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/ministry-data/${token}`, {
        ...form,
        consent_acknowledged: consent,
        turnstile_token: turnstileToken,
        website,
      });
      setDone(true);
    } catch (err) {
      // The backend names the field that is wrong — a mistyped CNP says so,
      // rather than "eroare de validare", which sends people hunting.
      toast.error(apiErrorMessage(err));
      turnstileRef.current?.reset?.();
      setTurnstileToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (page === null) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-sm text-[#6B7280]" data-testid="ministry-loading">
          <Loader2 size={16} className="animate-spin" /> Se încarcă…
        </div>
      </Shell>
    );
  }

  if (page === false) {
    return (
      <Shell>
        <div
          className="rounded-3xl border border-[#E5E7EB] bg-white p-8 md:p-10"
          data-testid="ministry-bad-link"
        >
          <h1 className="font-display text-2xl font-bold text-[#373A45]">
            Linkul nu mai este valabil.
          </h1>
          <p className="mt-4 text-[#4b4f5a] leading-relaxed">
            Linkul din email este valabil 30 de zile și poate fi folosit o singură dată
            pentru trimiterea datelor. Dacă ai nevoie de unul nou, scrie-ne la{" "}
            <a href="mailto:contact@fped.org" className="text-[#965EA7] font-semibold underline">
              contact@fped.org
            </a>{" "}
            și ți-l retrimitem.
          </p>
        </div>
      </Shell>
    );
  }

  // A Curs FPED enrolment: the data is for the contract and the certificate, not for the ministry.
  const fped = isFped(page);
  if (done || page.already_submitted) {
    return (
      <Shell fped={fped}>
        <div
          className="rounded-3xl border border-[#E5E7EB] bg-white p-8 md:p-10 text-center"
          data-testid="ministry-done"
        >
          <div className="mx-auto h-12 w-12 rounded-2xl bg-[#EAF4EE] text-[#2F6B4E] grid place-items-center">
            <CheckCircle2 size={24} />
          </div>
          <h1 className="mt-5 font-display text-2xl font-bold text-[#373A45]">
            Am primit datele tale.
          </h1>
          <p className="mt-4 text-[#4b4f5a] leading-relaxed">
            Îți mulțumim — atât ne trebuia pentru {fped ? "contract și certificat" : "dosarul de acreditare"}. Dacă observi
            că ceva nu este corect, scrie-ne la{" "}
            <a href="mailto:contact@fped.org" className="text-[#965EA7] font-semibold underline">
              contact@fped.org
            </a>{" "}
            și corectăm împreună.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell fped={fped}>
      <div className="rounded-3xl border border-[#E5E7EB] bg-white p-6 md:p-10">
        <h1 className="font-display text-2xl md:text-3xl font-bold text-[#373A45] !leading-tight">
          {fped ? "Datele pentru contract și certificat" : "Datele pentru dosarul de acreditare"}
        </h1>
        {fped ? (
          <p className="mt-4 text-[#4b4f5a] leading-relaxed" data-testid="ministry-intro-fped">
            Pentru contractul de participare la curs și pentru certificatul pe care îl primești la final
            avem nevoie de câteva date despre tine. Cele mai multe sunt deja completate
            cu ce ne-ai transmis la înscriere — verifică-le și completează restul.
          </p>
        ) : (
          <p className="mt-4 text-[#4b4f5a] leading-relaxed">
            Programul este acreditat de Ministerul Educației și Cercetării prin{" "}
            <strong>OMEC nr. 4712/19.08.2026</strong>. Pentru dosarul pe care îl depunem
            avem nevoie de câteva date despre tine. Cele mai multe sunt deja completate
            cu ce ne-ai transmis la înscriere — verifică-le și completează restul.
          </p>
        )}

        {(page.group_name || page.group_start_date) && (
          <div
            className="mt-6 rounded-2xl bg-[#F0E7F4]/60 px-5 py-4 text-sm text-[#373A45]"
            data-testid="ministry-enrollment-summary"
          >
            <strong>Înscrierea ta:</strong>{" "}
            {page.group_name || "grupă nestabilită încă"}
            {page.group_start_date ? ` · start ${page.group_start_date}` : ""}
          </div>
        )}

        {/* The information notice sits above the CNP field rather than behind a
            link, because the field it explains is the one people hesitate on. */}
        <div
          className="mt-6 rounded-2xl border-l-4 border-[#519872] bg-[#F5F6F6] px-5 py-4 text-sm text-[#373A45] leading-relaxed"
          data-testid="ministry-notice"
        >
          <div className="flex items-start gap-2">
            <FileText size={16} className="text-[#519872] shrink-0 mt-0.5" />
            <div>
              <strong>De ce îți cerem CNP-ul.</strong>{" "}
              {fped
                ? "Te identifică în contractul de participare la curs pe care îl semnezi cu noi și în evidența "
                  + "certificatelor eliberate, deci îl prelucrăm pentru încheierea și executarea contractului. "
                  + "Îl folosim exclusiv în aceste scopuri."
                : "Este cerut de Ministerul Educației pentru dosarul programului acreditat, deci îl "
                  + "prelucrăm în temeiul unei obligații legale, nu al consimțământului. Îl folosim "
                  + "exclusiv pentru acest dosar."}{" "}
              Detalii în{" "}
              <a
                href="/politica-confidentialitate"
                target="_blank"
                rel="noreferrer"
                className="text-[#965EA7] font-semibold underline"
              >
                politica de confidențialitate
              </a>.
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-6" data-testid="ministry-form">
          {/* Honeypot — off-screen, never announced, never focusable. */}
          <input
            type="text"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute left-[-9999px] w-px h-px opacity-0"
          />

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 md:items-start">
            <label className="block">
              <Label required>Nume de familie</Label>
              <input className={FIELD_CLS} value={form.nume} onChange={set("nume")}
                     required data-testid="ministry-nume" />
            </label>
            {/* Wide enough for the label rather than for the value: the input
                holds one letter, but "Inițiala tatălui" set in uppercase needs
                the room, and a label that wraps unaligns the whole row. */}
            <label className="block md:w-40">
              <Label required>Inițiala tatălui</Label>
              <input className={FIELD_CLS} value={form.initiala_tatalui}
                     onChange={set("initiala_tatalui")} maxLength={2} required
                     data-testid="ministry-initiala" />
            </label>
            <label className="block">
              <Label required>Prenume</Label>
              <input className={FIELD_CLS} value={form.prenume} onChange={set("prenume")}
                     required data-testid="ministry-prenume" />
            </label>
          </div>
          <p className="-mt-3 text-xs text-[#6B7280]">
            Scrie numele exact ca în actul de identitate, cu diacritice.
          </p>

          <label className="block">
            <Label required>CNP</Label>
            <input
              className={FIELD_CLS}
              value={form.cnp}
              onChange={set("cnp")}
              inputMode="numeric"
              maxLength={13}
              autoComplete="off"
              required
              aria-describedby={cnpLooksWrong ? "cnp-warning" : undefined}
              data-testid="ministry-cnp"
            />
            {/* A warning, never a block. The check digit catches most typos
                but not all, and CNPs from the late 1970s are reported to
                exist with incorrect ones — so somebody whose number really is
                this must be able to send it. Amber, not red, and the submit
                button stays live. */}
            {cnpLooksWrong && (
              <span
                id="cnp-warning"
                role="status"
                className="mt-2 flex items-start gap-2 rounded-lg bg-[#F59E0B]/10 px-3 py-2 text-xs text-[#92400E] leading-relaxed"
                data-testid="ministry-cnp-warning"
              >
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                {WARN_CHECKSUM}
              </span>
            )}
          </label>

          <label className="block">
            <Label required>Funcția didactică de predare</Label>
            <select
              className={FIELD_CLS}
              value={form.functie_didactica}
              onChange={set("functie_didactica")}
              required
              data-testid="ministry-functie"
            >
              <option value="">Selectează funcția</option>
              {(page.teaching_roles || []).map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <Label>Specializarea / disciplina predată</Label>
            <input className={FIELD_CLS} value={form.specializare} onChange={set("specializare")}
                   data-testid="ministry-specializare" />
          </label>

          <label className="block">
            <Label>Unitatea de învățământ</Label>
            <input className={FIELD_CLS} value={form.unitate} onChange={set("unitate")}
                   data-testid="ministry-unitate" />
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <Label>Localitate</Label>
              <input className={FIELD_CLS} value={form.city} onChange={set("city")}
                     data-testid="ministry-city" />
            </label>
            <label className="block">
              <Label>Județ</Label>
              <input className={FIELD_CLS} value={form.county} onChange={set("county")}
                     data-testid="ministry-county" />
            </label>
          </div>

          <label className="flex items-start gap-3 text-sm text-[#373A45] leading-relaxed">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 h-4 w-4 accent-[#965EA7]"
              data-testid="ministry-consent"
            />
            <span>
              Am citit informarea de mai sus și confirm că datele completate sunt corecte.
            </span>
          </label>

          <TurnstileField ref={turnstileRef} onVerify={setTurnstileToken} />

          <button
            type="submit"
            disabled={submitting}
            data-testid="ministry-submit"
            className="w-full rounded-full bg-[#965EA7] hover:bg-[#7A4986] disabled:opacity-60
                       text-white font-semibold text-base py-4 transition-colors"
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" /> Se trimite…
              </span>
            ) : "Trimite datele"}
          </button>

          <p className="flex items-center justify-center gap-2 text-xs text-[#6B7280]">
            <ShieldCheck size={14} className="text-[#519872]" />
            Nu îți cerem date de card, parole sau coduri primite prin SMS.
          </p>
        </form>
      </div>
    </Shell>
  );
}
